import React, { useState, useEffect, useRef } from "react";
import { appConfirmMessage } from '../../../utils/appConfirm';
import "../styles/ManagerWorkForce.css";
import { graphqlRequest } from "../../../utils/graphqlRequest";
import { toast } from "react-toastify";
import MemberDetailsModal from "./MemberDetailsModal";
import EditMemberModal from "./EditMemberModal";
import { useAdminListOverlay } from "../../../utils/useAdminPageSearch";

const MANAGER_ROLES = ["OPERATIONS_MANAGER", "SUPERVISOR"];
const PAGE_SIZE = 10;

const formatMember = (user, index) => ({
  id: user.employeeCode || `MGM${String(index + 1).padStart(3, "0")}`,
  dbId: user.id,
  name: `${user.firstName} ${user.lastName}`,
  title: "Manager",
  email: user.email,
  contact: user.contact || "N/A",
});

export default function ManagerWorkForce({ refreshKey = 0 }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [fullUserForDetails, setFullUserForDetails] = useState(null);
  const overlay = useAdminListOverlay(members, { idKey: "dbId" });
  const detailsMember = overlay.viewing;
  const editMember = overlay.editing;

  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  const fetchPage = async (pageNum, isInitial = false) => {
    if (!isInitial && loadingMoreRef.current) return;
    if (!isInitial) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const data = await graphqlRequest(
        `query GetAllUser($page: Int, $limit: Int, $roles: [String!]) {
          getAllUser(page: $page, limit: $limit, roles: $roles) {
            data { id firstName lastName email contact roles employeeCode }
            total page limit totalPages
          }
        }`,
        { page: pageNum, limit: PAGE_SIZE, roles: MANAGER_ROLES }
      );

      const result = data?.getAllUser;
      const formatted = (result?.data || []).map(formatMember);

      if (isInitial) {
        setMembers(formatted);
      } else {
        setMembers((prev) => {
          const existingIds = new Set(prev.map((m) => m.dbId));
          return [...prev, ...formatted.filter((m) => !existingIds.has(m.dbId))];
        });
      }

      pageRef.current = result?.page ?? pageNum;
      setHasMore((result?.page ?? pageNum) < (result?.totalPages ?? 1));
    } catch (err) {
      console.error("Error fetching manager members:", err);
      if (isInitial) setMembers([]);
    } finally {
      if (isInitial) setLoading(false);
      else { setLoadingMore(false); }
    }
  };

  useEffect(() => {
    setLoading(true);
    setMembers([]);
    setHasMore(false);
    pageRef.current = 1;
    fetchPage(1, true);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadingMoreRef.current = false;
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          fetchPage(pageRef.current + 1);
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, members.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (index) => {
    if (!await appConfirmMessage("Are you sure you want to delete this member?")) return;
    try {
      const member = members[index];
      await graphqlRequest(
        `mutation RemoveUser($id: String!) { removeUser(id: $id) { id } }`,
        { id: member.dbId }
      );
      setMembers((prev) => prev.filter((_, i) => i !== index));
      toast.success("Member deleted successfully!");
    } catch (err) {
      toast.error(err?.message || "Failed to delete member");
    }
  };

  const handleEdit = (index) => overlay.openEdit(members[index]);

  const handleViewDetails = (index) => overlay.openView(members[index]);

  useEffect(() => {
    const member = overlay.viewing;
    if (!member?.dbId) {
      setFullUserForDetails(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await graphqlRequest(
          `query GetSingleUser($GetUserInput: GetUserInput!) {
            getSingleUser(GetUserInput: $GetUserInput) {
              id firstName lastName email contact DOB address gender roles
              taxResidency bsb accountNumber taxFileNumber abnNumber
              superannuationAccountNumber emergencyContactName emergencyContactNumber
              claimTaxFreeThreshold jobTitle perHourRate superAnnuation salaryTerm
              typeOfWork paydayDay paydayTime joiningDate fundName spin fundABN
              fundTelephoneNumber memberNumber
            }
          }`,
          { GetUserInput: { id: member.dbId } }
        );
        if (!cancelled && data?.getSingleUser) setFullUserForDetails(data.getSingleUser);
      } catch (err) {
        console.error("Error fetching member details:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [overlay.viewing]);

  const refreshMembers = () => {
    setMembers([]);
    setHasMore(false);
    pageRef.current = 1;
    fetchPage(1, true);
  };

  return (
    <div className="Manager-WorkForce-Content">
      <h2 className="Manager-WorkForce-Heading">Manager Workforce</h2>
      <div className="Manager-WorkForce-TableWrapper">
        <table className="Manager-WorkForce-Table">
          <thead>
            <tr>
              <th>Employee id</th>
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              <th>Contact Number</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>Loading members...</td>
              </tr>
            ) : members.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>No manager members found</td>
              </tr>
            ) : (
              members.map((member, index) => (
                <tr key={member.dbId}>
                  <td>{member.id}</td>
                  <td>{member.name}</td>
                  <td>{member.title}</td>
                  <td>{member.email}</td>
                  <td>{member.contact}</td>
                  <td>
                    <button className="Manager-WorkForce-ViewDetails" onClick={() => handleViewDetails(index)}>View Details</button>
                    <button className="Manager-WorkForce-Edit" onClick={() => handleEdit(index)}>Edit</button>
                    <button className="Manager-WorkForce-Delete" onClick={() => handleDelete(index)}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && hasMore && <div ref={sentinelRef} style={{ height: "1px" }} />}
      {loadingMore && (
        <div style={{ textAlign: "center", padding: "10px", color: "#9ca3af", fontSize: "13px" }}>
          Loading more...
        </div>
      )}
      {detailsMember && (
        <MemberDetailsModal
          member={detailsMember}
          fullUser={fullUserForDetails}
          onClose={() => { overlay.closeOverlay(); setFullUserForDetails(null); }}
        />
      )}
      {editMember && (
        <EditMemberModal member={editMember} onClose={() => overlay.closeOverlay()} onSaved={refreshMembers} />
      )}
    </div>
  );
}
